import { faFile } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ListCard, Popup, Selectbox, Textbox } from "@streetsheaver/sh-ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";
import { EnvironmentContext } from "../../context/EnvironmentContextProvider";
import { openOrDownloadFile } from "../../helpers/DownloadFile";
import { callAPI } from "../../helpers/callAPI";
import { useTenantDatasources } from "../../hooks/useTenantDatasources";
import { useTenants } from "../../hooks/useTenants";
import "./ConversionSettings.scss";

const getInvalidConvSettings = (convSettings) => {
	const invalidArray = [];
	if (convSettings?.SHReportingDbConnectionString?.length === 0) invalidArray.push("connectionString");
	if (convSettings?.DataSourceIds?.length === 0) invalidArray.push("datasourceIds");
	return invalidArray;
};

export default function ConversionSettings() {
	const { selectedEnv } = useContext(EnvironmentContext);
	const { tenants, tenantsLoading } = useTenants();
	const tenantName = useParams().tenantName;
	const locationState = useLocation()?.state;
	const groupId = locationState?.groupId;
	const matchingTenant = tenants?.find((tenant) => {
		if (tenant.name !== tenantName) return false;
		if (groupId) return tenant.groupId === groupId;
		return true;
	});
	const repGenTenantId = locationState?.repGenId ?? matchingTenant?.reportGeneratorTenant;
	// For multi-org tenants, reportGeneratorTenant already includes the groupId (baseId-groupId)
	const tenantId = repGenTenantId;
	const baseTenantId =
		groupId && repGenTenantId?.endsWith(`-${groupId}`)
			? repGenTenantId.slice(0, -(groupId.length + 1))
			: repGenTenantId;
	const tenantInternalId = locationState?.id ?? matchingTenant?.id;
	const { datasources, datasourcesLoading } = useTenantDatasources(selectedEnv, tenantInternalId, repGenTenantId);
	const [conversionForm, setConversionForm] = useState({
		TenantId: baseTenantId,
		GroupId: groupId ?? null,
		SHReportingDbConnectionString: "",
		DataSourceIds: [],
	});
	const [formErrors, setFormErrors] = useState([]);
	const [showAddPopup, setShowAddPopup] = useState(false);
	const queryClient = useQueryClient();

	useEffect(() => {
		setConversionForm((prev) => ({ ...prev, TenantId: baseTenantId, GroupId: groupId ?? null }));
	}, [baseTenantId, groupId]);

	const { data: conversionSettings, isLoading: isLoadingConversionSettings } = useQuery({
		queryKey: ["ConversionSettings", selectedEnv, tenantId],
		queryFn: async () => {
			const res = await callAPI({ url: `tenants/${tenantId}/conversion-settings`, selectedEnv: selectedEnv });
			return res.data;
		},
		refetchOnWindowFocus: false,
		retry: (count, error) => {
			return error?.response?.status === 404;
		},
		enabled: !tenantsLoading && !datasourcesLoading,
	});

	const { data: conversionTool, isLoading: isLoadingConversionTool } = useQuery({
		queryKey: ["ConversionTool", selectedEnv, tenantId],
		queryFn: async () => {
			const res = await callAPI({ url: `conversion-tool`, selectedEnv: selectedEnv });
			return res.data;
		},
		refetchOnWindowFocus: false,
	});

	const addConversionSettings = useMutation({
		mutationFn: async () => {
			const invalidEntries = getInvalidConvSettings(conversionForm);
			setFormErrors(invalidEntries);
			if (invalidEntries?.length > 0) return;
			return await callAPI({
				url: `tenants/${baseTenantId}/conversion-settings`,
				method: "post",
				body: conversionForm,
				selectedEnv: selectedEnv,
			});
		},
		onSuccess: (res) => {
			queryClient.invalidateQueries(["ConversionSettings", selectedEnv, tenantId]);
			if (res) {
				setConversionForm({
					TenantId: baseTenantId,
					GroupId: groupId ?? null,
					SHReportingDbConnectionString: "",
					DataSourceIds: [],
				});
				setShowAddPopup(false);
			}
		},
	});

	const generateSettingsFile = useMutation({
		mutationFn: async (settingsId) => {
			const response = await callAPI({
				url: `tenants/${tenantId}/conversion-settings/${settingsId}/generate`,
				selectedEnv: selectedEnv,
			});
			const blob = new Blob([JSON.stringify(response.data)], { type: "application/json" });
			openOrDownloadFile(blob, "conversion.settings.json");
			return response;
		},
	});

	if (datasourcesLoading || isLoadingConversionSettings || isLoadingConversionTool)
		return <FontAwesomeIcon icon={faFile} spin />;

	const AddConversionSettingsContent = (
		<div className="AddConversionSettings">
			<h1>Add New Conversion Settings</h1>
			<Textbox
				title={"Connection String (SH Reporting Database you are converting from)"}
				value={conversionForm?.SHReportingDbConnectionString}
				onChange={(e) => setConversionForm((prev) => ({ ...prev, SHReportingDbConnectionString: e.target.value }))}
				isError={formErrors.includes("connectionString")}
				errorMessage={"Connection string cannot be empty"}
			/>
			<Selectbox
				title={"Selected Datasources (Datasources to convert into)"}
				items={datasources}
				displayExpr={"name"}
				onChange={(e) => setConversionForm((prev) => ({ ...prev, DataSourceIds: [e?.id] }))}
				isError={formErrors.includes("datasourceIds")}
				errorMessage={"Datasource to convert to must be selected"}
			/>
			<Button text={"Generate Conversion Settings"} unpadded onClick={() => addConversionSettings.mutate()} />
		</div>
	);

	return (
		<div className="ConversionSettings">
			<p>{conversionTool.notes}</p>
			<a href={conversionTool.downloadUrl} target="_blank" rel="noreferrer">
				{conversionTool.downloadUrl}
			</a>
			<div className="ConversionSettings__cards">
				{conversionSettings?.map((c, i) => {
					return (
						<ListCard
							key={i}
							title={`Version ${c?.version}`}
							subtext1={`Connection String: ${c?.connectionString}`}
							subtext2={
								c.dataSourceMap[-1] &&
								`Datasource Converting To: ${datasources.find((d) => d.id === c.dataSourceMap[-1])?.name}`
							}
							button1={{
								type: "CTA",
								label: "Download Settings",
								onClick: () => generateSettingsFile.mutate(c.id),
							}}
						/>
					);
				})}
			</div>
			<Button unpadded type={"CTA"} text={"Add new settings"} onClick={() => setShowAddPopup(true)} />
			<Popup
				renderContent={AddConversionSettingsContent}
				noHeader
				noPadding
				visible={showAddPopup}
				onOutsideClick={() => setShowAddPopup(false)}
			/>
		</div>
	);
}
